import { useAuth } from "../contexts/AuthContext";
import { useCallback, useEffect, useMemo, useState } from "react";

type Id = string;

interface SeenState {
  tasks: Set<Id>;
  dossiers: Set<Id>; // kenmerk
}

const buildStorageKeys = (userId: string | undefined) => ({
  tasksKey: `seen_tasks:${userId || "anon"}`,
  dossiersKey: `seen_dossiers:${userId || "anon"}`,
  firstSeenTasksKey: `first_seen_tasks:${userId || "anon"}`,
  firstSeenDossiersKey: `first_seen_dossiers:${userId || "anon"}`,
});

function parseStoredSet(value: string | null): Set<Id> {
  try {
    if (!value) return new Set();
    const arr = JSON.parse(value) as string[];
    return new Set(arr);
  } catch {
    return new Set();
  }
}

export function useSeenNew() {
  const { user } = useAuth();
  const userId = user?.id;
  const { tasksKey, dossiersKey, firstSeenTasksKey, firstSeenDossiersKey } =
    useMemo(() => buildStorageKeys(userId), [userId]);

  const [state, setState] = useState<SeenState>(() => ({
    tasks: parseStoredSet(
      typeof window !== "undefined"
        ? localStorage.getItem(buildStorageKeys(userId).tasksKey)
        : null,
    ),
    dossiers: parseStoredSet(
      typeof window !== "undefined"
        ? localStorage.getItem(buildStorageKeys(userId).dossiersKey)
        : null,
    ),
  }));

  const [firstSeenTasks, setFirstSeenTasks] = useState<Record<Id, string>>(
    () => {
      try {
        const raw = localStorage.getItem(
          buildStorageKeys(userId).firstSeenTasksKey,
        );
        return raw ? JSON.parse(raw) : {};
      } catch {
        return {};
      }
    },
  );

  const [firstSeenDossiers, setFirstSeenDossiers] = useState<
    Record<Id, string>
  >(() => {
    try {
      const raw = localStorage.getItem(
        buildStorageKeys(userId).firstSeenDossiersKey,
      );
      return raw ? JSON.parse(raw) : {};
    } catch {
      return {};
    }
  });

  // Reload when user changes
  useEffect(() => {
    const tasks = parseStoredSet(localStorage.getItem(tasksKey));
    const dossiers = parseStoredSet(localStorage.getItem(dossiersKey));
    setState({ tasks, dossiers });
    try {
      setFirstSeenTasks(
        JSON.parse(localStorage.getItem(firstSeenTasksKey) || "{}"),
      );
    } catch {
      setFirstSeenTasks({});
    }
    try {
      setFirstSeenDossiers(
        JSON.parse(localStorage.getItem(firstSeenDossiersKey) || "{}"),
      );
    } catch {
      setFirstSeenDossiers({});
    }
  }, [tasksKey, dossiersKey, firstSeenTasksKey, firstSeenDossiersKey]);

  // Helpers to persist
  const persist = useCallback(
    (type: "tasks" | "dossiers", set: Set<Id>) => {
      const key = type === "tasks" ? tasksKey : dossiersKey;
      localStorage.setItem(key, JSON.stringify(Array.from(set)));
      // Notify other hook instances in this tab
      try {
        window.dispatchEvent(
          new CustomEvent("seen-updated", { detail: { key } }),
        );
      } catch {}
    },
    [tasksKey, dossiersKey],
  );

  // Public API
  const isTaskNew = useCallback(
    (taskId: Id | undefined | null): boolean => {
      if (!taskId) return false;
      return !state.tasks.has(taskId);
    },
    [state.tasks],
  );

  const markTaskSeen = useCallback(
    (taskId: Id | undefined | null) => {
      if (!taskId) return;
      setState((prev) => {
        if (prev.tasks.has(taskId)) return prev;
        const next = new Set(prev.tasks);
        next.add(taskId);
        const newState = { ...prev, tasks: next };
        persist("tasks", next);
        return newState;
      });
    },
    [persist],
  );

  const isDossierNew = useCallback(
    (kenmerk: Id | undefined | null): boolean => {
      if (!kenmerk) return false;
      return !state.dossiers.has(kenmerk);
    },
    [state.dossiers],
  );

  const markDossierSeen = useCallback(
    (kenmerk: Id | undefined | null) => {
      if (!kenmerk) return;
      setState((prev) => {
        if (prev.dossiers.has(kenmerk)) return prev;
        const next = new Set(prev.dossiers);
        next.add(kenmerk);
        const newState = { ...prev, dossiers: next };
        persist("dossiers", next);
        return newState;
      });
    },
    [persist],
  );

  // Sync across components/tabs when localStorage changes or when custom events fire
  useEffect(() => {
    const reload = () => {
      const tasks = parseStoredSet(localStorage.getItem(tasksKey));
      const dossiers = parseStoredSet(localStorage.getItem(dossiersKey));
      setState({ tasks, dossiers });
      try {
        setFirstSeenTasks(
          JSON.parse(localStorage.getItem(firstSeenTasksKey) || "{}"),
        );
      } catch {
        /* noop */
      }
      try {
        setFirstSeenDossiers(
          JSON.parse(localStorage.getItem(firstSeenDossiersKey) || "{}"),
        );
      } catch {
        /* noop */
      }
    };

    const onStorage = (e: StorageEvent) => {
      if (!e.key) return;
      if (
        [
          tasksKey,
          dossiersKey,
          firstSeenTasksKey,
          firstSeenDossiersKey,
        ].includes(e.key)
      )
        reload();
    };
    const onCustom = () => reload();

    window.addEventListener("storage", onStorage);
    window.addEventListener("seen-updated", onCustom as EventListener);
    return () => {
      window.removeEventListener("storage", onStorage);
      window.removeEventListener("seen-updated", onCustom as EventListener);
    };
  }, [tasksKey, dossiersKey, firstSeenTasksKey, firstSeenDossiersKey]);

  const getTaskFirstSeen = useCallback(
    (taskId: Id | undefined | null): Date | undefined => {
      if (!taskId) return undefined;
      const existing = firstSeenTasks[taskId];
      return existing ? new Date(existing) : undefined;
    },
    [firstSeenTasks],
  );

  const getDossierFirstSeen = useCallback(
    (kenmerk: Id | undefined | null): Date | undefined => {
      if (!kenmerk) return undefined;
      const existing = firstSeenDossiers[kenmerk];
      return existing ? new Date(existing) : undefined;
    },
    [firstSeenDossiers],
  );

  const getOrInitTaskFirstSeen = useCallback(
    (taskId: Id): Date => {
      if (!taskId) return new Date();
      const existing = firstSeenTasks[taskId];
      if (existing) return new Date(existing);
      const nowIso = new Date().toISOString();
      // Functional update: callers (e.g. NotificationTray useMemo) may init many ids in one
      // pass; stale-closure spreads would only keep one id per render and can exceed React's
      // max update depth.
      setFirstSeenTasks((prev) => {
        if (prev[taskId]) return prev;
        const next = { ...prev, [taskId]: nowIso };
        localStorage.setItem(firstSeenTasksKey, JSON.stringify(next));
        return next;
      });
      return new Date(nowIso);
    },
    [firstSeenTasks, firstSeenTasksKey],
  );

  const getOrInitDossierFirstSeen = useCallback(
    (kenmerk: Id): Date => {
      if (!kenmerk) return new Date();
      const existing = firstSeenDossiers[kenmerk];
      if (existing) return new Date(existing);
      const nowIso = new Date().toISOString();
      setFirstSeenDossiers((prev) => {
        if (prev[kenmerk]) return prev;
        const next = { ...prev, [kenmerk]: nowIso };
        localStorage.setItem(firstSeenDossiersKey, JSON.stringify(next));
        return next;
      });
      return new Date(nowIso);
    },
    [firstSeenDossiers, firstSeenDossiersKey],
  );

  return {
    isTaskNew,
    markTaskSeen,
    isDossierNew,
    markDossierSeen,
    getTaskFirstSeen,
    getDossierFirstSeen,
    getOrInitTaskFirstSeen,
    getOrInitDossierFirstSeen,
  };
}

export type { SeenState };
